import Storage from '@aws-amplify/storage';
import axios from 'axios';
import { S3_PUBLIC_URL, S3_PUBLIC_URL_OLD } from '../config/constants';

const extensionToMime = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  svg: 'image/svg+xml',
  gif: 'image/gif',
  webp: 'image/webp',
  pdf: 'application/pdf',
  txt: 'text/plain',
  ttf: 'font/ttf',
  otf: 'font/otf',
  woff: 'font/woff',
  woff2: 'font/woff2'
};

const getExtensionFromMime = (mime) => {
  const entry = Object.entries(extensionToMime).find(
    ([_, value]) => value === mime
  );
  return entry ? entry[0] : '';
};

const uploadFileToStorage = async (
  identifier,
  file,
  onSuccess,
  onError,
  config = {}
) => {
  try {
    const nameParts = file.name.split('.');
    const hasExtension = nameParts.length > 1;
    let extension = hasExtension ? nameParts.pop().toLowerCase() : '';
    if (!extension || !extensionToMime[extension]) {
      extension = getExtensionFromMime(file.type) || 'bin';
    }

    const contentType =
      file.type || extensionToMime[extension] || 'application/octet-stream';

    const fileName = `${identifier.replace(' ', '-')}${Math.round(
      new Date().getTime() / 1000
    )}.${extension}`;

    const uploadData = await Storage.put(fileName, file, {
      ...config,
      contentType
    });

    if (uploadData) {
      onSuccess(`${S3_PUBLIC_URL}${uploadData.key}`, file.name);
    }
  } catch (err) {
    console.error('Error uploading file:', err);
    if (onError) {
      onError();
    }
  }
};

const dataUrlToFile = async (dataUrl, fileName, onSuccess, onError) => {
  try {
    const res = await fetch(dataUrl);
    const blob = await res.blob();
    if (onSuccess) {
      onSuccess(new File([blob], fileName, { type: 'image/png' }));
    }
  } catch (err) {
    console.error('Error converting file:', err);
    if (onError) {
      onError();
    }
  }
};

const urlToDataURL = (url, callback) => {
  const xhr = new XMLHttpRequest();
  xhr.open('GET', url);
  xhr.responseType = 'blob';
  xhr.onload = () => {
    const reader = new FileReader();
    reader.onloadend = () => {
      callback(reader.result);
    };
    reader.readAsDataURL(xhr.response);
  };
  xhr.send();
};

const downloadBlob = (blob, fileName) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.style.display = 'none';
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
};

const downloadUrlFile = (
  fileUrl,
  fileName,
  fileType = null,
  onErrorCallback = null
) => {
  if (fileName) {
    let downloadFileName = fileName;
    const nameParts = downloadFileName.split('.');
    const hasExtension = nameParts.length > 1;
    let extension = hasExtension ? nameParts.pop().toLowerCase() : '';
    if (fileType && (!extension || !extensionToMime[extension])) {
      extension = getExtensionFromMime(fileType) || 'bin';
      downloadFileName = `${fileName}.${extension}`;
    }

    fetch(fileUrl)
      .then((resp) => resp.blob())
      .then((blob) => {
        downloadBlob(blob, fileName);
      })
      .catch((e) => {
        console.log('Error downloading File');
        if (onErrorCallback) {
          onErrorCallback(e);
        }
      });
  }
  else {
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = fileUrl;
    document.body.appendChild(a);
    a.click();
  }
};

const fileToBase64String = (file, callback) => {
  const reader = new FileReader();

  reader.onload = (event) => {
    callback(event.target.result);
  };

  reader.onerror = (error) => {
    console.log('Error: ', error);
  };

  reader.readAsDataURL(file);
};

const deleteFileFromStorage = async (url) => {
  try {
    const result = await Storage.remove(
      url.replace(S3_PUBLIC_URL, '').replace(S3_PUBLIC_URL_OLD, '')
    );
  } catch (err) {
    console.error('Error deleting file:', err);
  }
};

const uploadFileContentRecursively = (
  media,
  identifier,
  onSuccess,
  onError
) => {
  if (!media.some((m) => m.file && !m.src && !m.deleted)) {
    onSuccess(media);
  }
  else {
    for (const m of media) {
      if (m.file && !m.src && !m.deleted) {
        uploadFileToStorage(
          `${identifier}-${new Date().getTime()}`,
          m.file,
          (location) => {
            m.file = null;
            m.src = location;
            uploadFileContentRecursively(
              [...media],
              identifier,
              onSuccess,
              onError
            );
          },
          onError
        );
        break;
      }
    }
  }
};

const fetchMediaContent = async (urls) => {
  const getContentType = async (url) => {
    try {
      const response = await axios.head(url, { maxRedirects: 10 });
      return { url, contentType: response.headers['content-type'] };
    } catch (error) {
      return { url, contentType: `Error: ${error.message}` };
    }
  };

  const promises = urls.map((url) => getContentType(url));
  return Promise.all(promises);
};

export {
  dataUrlToFile,
  deleteFileFromStorage,
  downloadBlob,
  downloadUrlFile,
  urlToDataURL,
  uploadFileToStorage,
  fileToBase64String,
  uploadFileContentRecursively,
  fetchMediaContent
};
